/** Shared navigation — Layout, command palette, breadcrumbs */

export type NavItem = { to: string; label: string; icon: string }

/**
 * platform: which bucket controls visibility of this section
 *   'dqa'      → DQA Platform access required
 *   'anomaly'  → Anomaly Detection Platform access required
 *   'vv'       → V&V Platform access required
 *   'reviewer' → Reviewer Platform access required
 *   'admin'    → Admin / Super Admin only
 *   null       → always visible to all authenticated users
 * Admin and Super Admin always see ALL sections regardless of platform field.
 */
export type NavSection = {
  section: string
  items: NavItem[]
  platform: 'dqa' | 'anomaly' | 'vv' | 'reviewer' | 'admin' | null
}

export const NAV: NavSection[] = [
  {
    section: 'DQA PLATFORM', platform: 'dqa', items: [
      { to: '/dashboard',       label: 'Dashboard',        icon: 'home' },
      { to: '/projects',        label: 'Projects',         icon: 'folder' },
      { to: '/datasets',        label: 'Datasets',         icon: 'database' },
      { to: '/rules',          label: 'Rule Manager',     icon: 'settings' },
      { to: '/rule-studio',    label: 'Correction Manager', icon: 'layers' },
      { to: '/run',            label: 'Run DQA',          icon: 'play' },
      { to: '/violations',     label: 'Violations',       icon: 'flag' },
      { to: '/corrections',    label: 'Workbench',        icon: 'edit' },
    ],
  },
  {
    section: 'ANOMALY DETECTION PLATFORM', platform: 'anomaly', items: [
      { to: '/trends',         label: 'Trend Monitor',     icon: 'bar-chart' },
      { to: '/anomaly',        label: 'Anomaly Detection', icon: 'alert-triangle' },
      { to: '/knowledge-base', label: 'Knowledge Base',    icon: 'book-open' },
      { to: '/ai',             label: 'AI Model Hub',      icon: 'layers' },
      { to: '/model-hub',      label: 'ML Model Hub',      icon: 'cpu' },
    ],
  },
  {
    section: 'AUTOMATION', platform: 'dqa', items: [
      { to: '/schedules', label: 'Schedules', icon: 'play' },
    ],
  },
  {
    section: 'REVIEWER PLATFORM', platform: 'reviewer', items: [
      { to: '/reviewer',             label: 'Reviewer Dashboard',   icon: 'home' },
      { to: '/reviewer/incoming',    label: 'Incoming Assignments', icon: 'download' },
      { to: '/reviewer/assignments', label: 'My Assignments',       icon: 'user' },
      { to: '/reviewer/cars',        label: 'CAR Management',       icon: 'alert-triangle' },
      { to: '/reviewer/statements',  label: 'Statements Archive',   icon: 'file-text' },
      { to: '/reviewer/integration', label: 'Integration Hub',      icon: 'activity' },
      { to: '/reviewer/connectors',  label: 'Registry Connectors',  icon: 'layers' },
    ],
  },
  {
    section: 'V&V PLATFORM', platform: 'vv', items: [
      { to: '/vv',            label: 'V&V Dashboard',      icon: 'home' },
      { to: '/vv/projects',   label: 'Projects',           icon: 'folder' },
      { to: '/vv/benchmarks', label: 'Benchmark Dashboard',icon: 'bar-chart' },
      { to: '/protocols',     label: 'Protocol Manager',   icon: 'layers' },
    ],
  },
  {
    section: 'COMPLIANCE', platform: null, items: [
      { to: '/audit',   label: 'Audit Trail', icon: 'file-text' },
      { to: '/reports', label: 'Reports',     icon: 'download' },
    ],
  },
  {
    section: 'ADMIN', platform: 'admin', items: [
      { to: '/settings', label: 'Settings',        icon: 'shield' },
      { to: '/users',    label: 'User Management', icon: 'users' },
      { to: '/status',   label: 'System Status',   icon: 'activity' },
    ],
  },
  {
    section: 'HELP', platform: null, items: [
      { to: '/manual', label: 'User Manual', icon: 'book' },
    ],
  },
]

export const PAGE_TITLES: Record<string, [string, string]> = {
  '/dashboard':      ['Dashboard',            'CO₂ Sequestration — DQA-STR1-TDD-001 · Real-time quality monitoring'],
  '/projects':       ['Project Management',   'Create, edit, archive and manage all CO₂ DQA projects'],
  '/datasets':       ['Datasets',             'Upload and manage sensor data sources'],
  '/rule-studio':    ['Correction Manager',     'Correction strategies paired to detection rules — every rule and its fix, side by side'],
  '/rules':          ['Rule Manager',         '28 active rules across 8 quality dimensions'],
  '/run':            ['Run DQA',              'Execute quality assessment against a dataset'],
  '/violations':     ['Violation Browser',    'Explore, filter and triage data quality violations'],
  '/corrections':    ['Correction Workbench', 'Review AI-generated and rule-based corrections'],
  '/correctionrules':['Correction Rules',     'Configure rule-based correction strategies'],
  '/schedules':      ['Scheduled Runs',       'Automate DQA runs on a cron schedule per project'],
  '/anomaly':        ['Anomaly Detection',    'Three-model ensemble: Heuristic · Statistical · ML'],
  '/knowledge-base': ['Knowledge Base',       'Operational knowledge for GenAI recommendations'],
  '/ai':             ['AI Model Hub',         'Model training, lifecycle and performance analytics'],
  '/model-hub':      ['ML Model Hub',         'XGBoost + Isolation Forest status · Nightly retraining · SHAP explainability'],
  '/ai-chat':        ['AI Chat',              'Ask questions about your DQA data in natural language'],
  '/trends':         ['Trend Monitor',        'Historical quality trends and anomaly detection'],
  '/reviewer/incoming':    ['Incoming Assignments', 'Unassigned verification projects from registries — assign to reviewers'],
  '/reviewer':             ['Reviewer Dashboard',   'Overview — assignments, open CARs, deadlines and statements at a glance'],
  '/reviewer/assignments': ['My Assignments',      'Third-party reviewer assignment inbox — accept, plan, and execute V&V reviews'],
  '/reviewer/cars':        ['CAR Management',      'Cross-project Corrective Action Requests — track findings and company responses'],
  '/reviewer/statements':  ['Statements Archive',  'All issued verification statements — signed, countersigned, and submitted'],
  '/reviewer/integration': ['Integration Hub',     'Registry and DMRV event log — inbound assignments, outbound submissions'],
  '/reviewer/connectors':  ['Registry Connectors', 'API configuration for connected registries — Isometric, Puro.Earth, and more'],
  '/reviewer/review':      ['Active Review',       'End-to-end verification workspace — plan, evidence, CARs, and opinion'],
  '/vv':             ['V&V Dashboard',          'Overview — project stats, registry summary, expiry alerts and recent activity'],
  '/vv/projects':    ['Verification Projects',  'All V&V projects — create, manage and track verification engagements'],
  '/protocols':      ['Protocol Manager',      'Living Protocol Registry — 4 registries, 16 protocols, AI-assisted updates'],
  '/vv/project':     ['Verification Project',  'Registry compliance review and checkpoint analysis'],
  '/audit':          ['Audit Trail',           'Immutable compliance log of all platform actions'],
  '/reports':        ['Reports & Export',      'Compliance-ready reports and corrected dataset exports'],
  '/settings':       ['Settings',              'Platform configuration and preferences'],
  '/users':          ['User Management',       'Manage platform users, roles, permissions and platform access'],
  '/status':         ['System Status',         'Health check for all platform services — Backend, DB, S3, LLM'],
  '/manual':         ['User Manual',           'Full platform documentation, workflows and technical reference'],
  '/onboarding':     ['Getting Started',       'Role-specific setup guide for new team members'],
}

export type FlatNavItem = NavItem & { section: string }

export function getFlatNavItems(): FlatNavItem[] {
  return NAV.flatMap(s => s.items.map(item => ({ ...item, section: s.section })))
}

export function isNavActive(pathname: string, itemTo: string): boolean {
  if (pathname === itemTo) return true
  // Index/dashboard routes — only active on exact match, never on sub-paths
  // e.g. /reviewer should not highlight when on /reviewer/incoming
  const exactMatchOnly = ['/dashboard', '/reviewer', '/vv', '/schedules']
  if (exactMatchOnly.includes(itemTo)) return false
  return pathname.startsWith(itemTo + '/')
}

/** Page title + subtitle for the shell header (supports dynamic V&V project routes). */
export function getHeaderCopy(pathname: string, routeParams: { id?: string }): [string, string] {
  if (pathname.startsWith('/vv/project/') && routeParams.id) {
    const id = routeParams.id
    const short = id.length > 14 ? `${id.slice(0, 10)}…` : id
    const base = PAGE_TITLES['/vv/project'] || ['Verification Project', '']
    return [base[0], `${base[1]} · ${short}`]
  }
  const exact = PAGE_TITLES[pathname]
  if (exact) return exact
  const keys = Object.keys(PAGE_TITLES).sort((a, b) => b.length - a.length)
  for (const p of keys) {
    if (pathname === p || pathname.startsWith(`${p}/`)) return PAGE_TITLES[p]!
  }
  return ['DataSentinel', '']
}
